import React, { useState, useEffect, useCallback } from 'react';
import './FeaturesSection.css';

// Hoisted static data so React does not recreate on parent re-renders.
const FEATURES_STATIC = [
  { id: 'syllabus', icon: '📄', title: 'Syllabus Upload', description: 'Drop your syllabus. Scout auto-builds your semester plan in minutes.' },
  { id: 'calendar', icon: '🗓️', title: 'Calendar Sync', description: "Your semester's schedule, simplified." },
  { id: 'dashboard', icon: '📊', title: 'Smart Dashboard', description: 'Deadlines, tasks, and study blocks prioritized for you.' },
  { id: 'chat', icon: '💬', title: 'AI Doc Chat', description: 'Ask PDFs anything—answers with citations and steps.' }
];

// Memoized grid with panel wrapper & staged tiles for cascade animation
const FeatureGrid = React.memo(function FeatureGrid({ openTileId, toggleTile }) {
  // Step 1: prove render path
  // eslint-disable-next-line no-console
  console.log('[FeatureGrid] render openTileId =', openTileId);
  return (
    <div className="feature-grid-panel" data-panel>
      <p className="tap-hint" aria-hidden="true">Tap a tile to learn more.</p>
      <div className="feature-grid quadrant-fit tiles-v2" role="list" aria-label="Feature tiles" data-tiles>
        {FEATURES_STATIC.map(({ id, icon, title, description }, i) => {
          const open = openTileId === id;
          const descId = `feature-desc-${id}`;
          return (
            <div
              key={id}
              role="listitem"
              className={"fg-tile" + (open ? ' open' : '')}
              style={{ '--tile-order': i }}
              tabIndex={0}
              aria-controls={descId}
              aria-label={(open ? 'Hide description for ' : 'Show description for ') + title}
              onClick={() => toggleTile(id)}
              onKeyDown={e => { if(e.key==='Enter' || e.key===' '){ e.preventDefault(); toggleTile(id);} }}
              data-tile
            >
              {open ? (
                <div className="fg-open replaced" id={descId}>
                  {/* Description replaces icon + title visually. Keep title accessible for screen readers. */}
                  <p className="fg-desc only-desc" role="note">{description}</p>
                  <span className="visually-hidden" aria-hidden="false">{title}</span>
                </div>
              ) : (
                <div className="fg-closed">
                  <div className="fg-icon" aria-hidden>{icon}</div>
                  <div className="fg-title">{title}</div>
                </div>
              )}
            </div>
          );
        })}
        <div className="scout-signature" aria-hidden>🐾 <span className="scout-sig-text">Powered by Scout — your semester’s smartest companion.</span></div>
      </div>
    </div>
  );
});

export default function FeaturesSection(){

  const accentWords = ['Organized.', 'Smarter.', 'Supercharged.'];
  const [accentIndex, setAccentIndex] = useState(accentWords.length - 1);
  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    const id = setInterval(() => setAccentIndex(i => (i + 1) % accentWords.length), 5000);
    return () => clearInterval(id);
  }, [accentWords.length]);

  // Open tile state & toggle (single open)
  const [openTileId, setOpenTileId] = useState(null);
  const toggleTile = useCallback((id) => {
    setOpenTileId(prev => {
      const next = prev === id ? null : id;
      // eslint-disable-next-line no-console
      console.log('[toggleTile]', { clicked:id, prev, next });
      return next;
    });
  }, []);

  // Disabled tile cascade animations for debugging disappearing text.

  // Expansion disabled per latest request – keep placeholder state if future re-enable is desired
  // Expansion disabled; tiles static.

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    const targets = document.querySelectorAll('[data-animate="features-headline"], .helper-text');
    const section = document.getElementById('features');
    if (!targets.length) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible');
          if (section && e.target === targets[0]) { section.classList.add('is-active'); }
        }
      });
    }, { threshold: 0.2 });
    targets.forEach(t => obs.observe(t));
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const chip = document.querySelector('.scroll-chip, .scroll-chip-premium');
    if (!chip) return;
    const handler = () => {
      const section = document.getElementById('features');
      if (!section) return;
      section.classList.remove('delayed-reveal');
      section.classList.add('delayed-reveal-pending');
      setTimeout(() => {
        section.classList.remove('delayed-reveal-pending');
        section.classList.add('delayed-reveal');
      }, 180);
    };
    chip.addEventListener('click', handler);
    return () => chip.removeEventListener('click', handler);
  }, []);


  return (
  <section id="features" className="sem-section snap-section" aria-labelledby="sem-headline" data-section="features">
      <div className="sem-wrap enhanced" data-animate="features-headline">
        <h2 id="sem-headline" className="sem-headline gradient-text">
          <span className="hs-plain">Your Semester,</span>
          <span className="hs-accent animated-accent" data-word={accentWords[accentIndex]}>{accentWords[accentIndex]}</span>
        </h2>
        <p className="sem-sub helper-text" aria-describedby="sem-headline">Four tools. One AI companion. Scout keeps everything synced, searchable, and stress-free.</p>
        <FeatureGrid openTileId={openTileId} toggleTile={toggleTile} />
      </div>
    </section>
  );
}
